import { ComponentPropsWithoutRef, ReactNode, useCallback, useMemo } from 'react';

import classNames from 'classnames';
import { Command } from 'cmdk';
import { Popover } from 'radix-ui';

import { comboboxStyles } from './combobox.styles';
import { ComboboxProvider } from './ComboboxContext';
import { ComboboxButtonTrigger } from './components/ComboboxButtonTrigger';
import { ComboboxChevron } from './components/ComboboxChevron';
import { ComboboxClearButton } from './components/ComboboxClearButton';
import { ComboboxDropdown } from './components/ComboboxDropdown';
import { ComboboxEmpty } from './components/ComboboxEmpty';
import { ComboboxGroup } from './components/ComboboxGroup';
import { ComboboxGroupLabel } from './components/ComboboxGroupLabel';
import { ComboboxInputTrigger } from './components/ComboboxInputTrigger';
import { ComboboxLoading } from './components/ComboboxLoading';
import { ComboboxOption } from './components/ComboboxOption';
import { ComboboxOptions } from './components/ComboboxOptions';
import { ComboboxPlaceholder } from './components/ComboboxPlaceholder';
import { ComboboxSearch } from './components/ComboboxSearch';
import { ComboboxSelectedOption } from './components/ComboboxSelectedOption';
import { ComboboxSeparator } from './components/ComboboxSeparator';
import { ComboboxTrigger } from './components/ComboboxTrigger';
import { useCombobox } from './useCombobox';
import type { ComboboxStore } from './useCombobox';

type ComboboxSlots = ReturnType<typeof comboboxStyles>;

export interface ComboboxProps
  extends Omit<React.ComponentProps<typeof Command>, 'className' | 'children' | 'label'> {
  children?: ReactNode;
  store?: ComboboxStore;
  onOptionSubmit?: (value: string) => void;
  className?: string;
  classNames?: Partial<Record<keyof ComboboxSlots, string>>;
  required?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  label?: ReactNode;
  labelProps?: ComponentPropsWithoutRef<'span'>;
  description?: ReactNode;
  descriptionProps?: ComponentPropsWithoutRef<'span'>;
  errorMessage?: ReactNode;
  errorMessageProps?: ComponentPropsWithoutRef<'span'>;
}

const ComboboxRoot = ({
  children,
  store: controlledStore,
  onOptionSubmit,
  disabled,
  invalid,
  className,
  classNames: classNamesProp,
  required,
  label,
  labelProps,
  description,
  descriptionProps,
  errorMessage,
  errorMessageProps,
  shouldFilter = false,
  loop = true,
  vimBindings = false,
  ...commandProps
}: ComboboxProps) => {
  const uncontrolledStore = useCombobox();
  const store = controlledStore || uncontrolledStore;
  const styles = comboboxStyles();

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        store.openDropdown();
      } else {
        store.closeDropdown();
      }
    },
    [store]
  );

  const contextValue = useMemo(
    () => ({
      store,
      classNames: classNamesProp,
      onOptionSubmit,
      disabled,
      invalid: invalid || !!errorMessage,
    }),
    [store, classNamesProp, onOptionSubmit, disabled, invalid, errorMessage]
  );

  return (
    <ComboboxProvider value={contextValue}>
      <Popover.Root open={store.dropdownOpened} onOpenChange={handleOpenChange}>
        <div className={classNames(styles.base(), classNamesProp?.base, className)}>
          {label && (
            <div className="mb-1">
              <span
                {...labelProps}
                className={classNames(
                  styles.label(),
                  classNamesProp?.label,
                  labelProps?.className
                )}
              >
                {label}
              </span>
              {required && <span className="text-red-6 ml-1">*</span>}
            </div>
          )}
          <Command
            data-slot="control"
            className={classNames(styles.control(), classNamesProp?.control)}
            shouldFilter={shouldFilter}
            loop={loop}
            vimBindings={vimBindings}
            {...commandProps}
          >
            {children}
          </Command>
          {description && (
            <span
              {...descriptionProps}
              className={classNames(
                styles.description(),
                classNamesProp?.description,
                descriptionProps?.className
              )}
            >
              {description}
            </span>
          )}
          {errorMessage && (
            <span
              {...errorMessageProps}
              className={classNames(
                styles.errorMessage(),
                classNamesProp?.errorMessage,
                errorMessageProps?.className
              )}
            >
              {errorMessage}
            </span>
          )}
        </div>
      </Popover.Root>
    </ComboboxProvider>
  );
};

export const Combobox = Object.assign(ComboboxRoot, {
  Trigger: ComboboxTrigger,
  Placeholder: ComboboxPlaceholder,
  ButtonTrigger: ComboboxButtonTrigger,
  InputTrigger: ComboboxInputTrigger,
  SelectedOption: ComboboxSelectedOption,
  Dropdown: ComboboxDropdown,
  Search: ComboboxSearch,
  Group: ComboboxGroup,
  Separator: ComboboxSeparator,
  Options: ComboboxOptions,
  Option: ComboboxOption,
  Label: ComboboxGroupLabel,
  Loading: ComboboxLoading,
  Empty: ComboboxEmpty,
  ClearButton: ComboboxClearButton,
  Chevron: ComboboxChevron,
});
